/**
 * The two ceremonies as PURE functions of one time.
 *
 * Nothing here keeps a timer. The results beat's clock (`beat.ts`) hands every
 * view the same `t`, and each view draws `rankFrameAt(plan, t)` /
 * `levelFrameAt(plan, t)`. A seek, a skip, a stalled main thread and a frozen
 * capture clock all land on the same frame, and no element can run ahead of
 * another — which is the whole reason this is a function of `t` and not a
 * stack of tweens. Blockari learned it the hard way over critique rounds
 * 54–57: a CSS delay and a JS timer are never the same time.
 *
 * ## The card
 *
 * The badge is the card, so the ceremony's one prop is a card: it rises off
 * the tray, spins about its own vertical axis, and the face it wears changes
 * at the instant it is EDGE-ON — the geometry is a real box with thickness, so
 * that instant is a gilt hairline rather than a scale-to-zero cheat. Yaw is
 * absolute degrees with the front face toward the camera at 0:
 *
 *   yaw ≡ 0   (mod 360)  the face
 *   yaw ≡ 90  (mod 180)  edge-on — nothing of either face is showing
 *   yaw ≡ 180 (mod 360)  the back
 *
 * so the swap sits exactly on 810° and the landing exactly on 1080°: the card
 * goes face → edge (the swap) → back → edge → the NEW face. Every angle below
 * is chosen to keep those two congruences true.
 */

import type { Rank } from '@/game/xp';
import type { CeremonyPlan } from './plan';

// --- easing ------------------------------------------------------------------

export const clamp01 = (x: number): number => (x <= 0 ? 0 : x >= 1 ? 1 : x);
/** 0 → 1 as t runs a → b. */
export const span = (t: number, a: number, b: number): number => clamp01((t - a) / (b - a));
export const easeOutCubic = (u: number): number => 1 - (1 - u) ** 3;
export const easeInCubic = (u: number): number => u * u * u;
export const easeInOutCubic = (u: number): number =>
  u < 0.5 ? 4 * u * u * u : 1 - (-2 * u + 2) ** 3 / 2;
/** Back-ease out (overshoot `s`). */
export const easeOutBack = (u: number, s = 1.7): number => {
  const v = u - 1;
  return 1 + (s + 1) * v * v * v + s * v * v;
};

/** The card's resting pose (degrees): square to the camera, tipped a touch back. */
export const REST_YAW = -8;
export const REST_PITCH = 9;

/** The card's finish, coarsest to richest. One per rank — see `FINISH_BY_RANK`. */
export type Finish = 'paper' | 'linen' | 'silver' | 'rose' | 'gold' | 'holo' | 'spectrum';

/**
 * Deuce and Seven are printed paper; the middle ranks are pressed linen; the
 * court cards are struck in metal, warming as they climb; Ace and Blackjack
 * are holographic; Wild is spectrum foil. Adam's ladder, Blockari's rule that
 * the finish — not just the hue — has to say how far up you are.
 */
export const FINISH_BY_RANK: readonly Finish[] = [
  'paper', // Deuce
  'paper', // Seven
  'linen', // Ten
  'silver', // Jack
  'rose', // Queen
  'gold', // King
  'holo', // Ace
  'holo', // Blackjack
  'spectrum', // Wild
];

export function finishFor(rank: Rank): Finish {
  return FINISH_BY_RANK[Math.min(Math.max(0, rank.index), FINISH_BY_RANK.length - 1)] ?? 'paper';
}

// --- the rank-up ---------------------------------------------------------------

/**
 * The rank-up's beats (ms from the crossing: the count reaching the new rank's
 * threshold). CONTINUE is up at `cont`; the end state is `end`. The rhythm is
 * Blockari's, so the two games' ceremonies feel like one family.
 */
export const RANK_T = {
  /** The veil rises over the results (old rank colour). */
  veilIn: 300,
  /** The old name comes in once the veil covers the results, then leaves before the swap. */
  oldIn: [200, 320],
  oldOut: [360, 500],
  /** The bar overflows (full, glowing), then leaves before the swap. */
  barOverflow: [100, 520],
  barOut: [520, 660],
  /** The card lifts off the tray and the spin winds up — moving from the first frames. */
  rise: 160,
  /** The card's edge gilds and the face goes white-hot from here to the swap. */
  heat: 560,
  /** THE SWAP: edge-on and white-hot. The face, the finish and the veil change here. */
  swap: 1000,
  /** The shockwave (and with it the recolour wipe) reaches the farthest corner. */
  wipe: 520,
  /** The heat has left the new finish. */
  cool: 1440,
  /** The four suits of the new rank fan out behind the card. */
  fan: [1000, 1640],
  /** A riffle of cards crosses the camera. */
  riffle: [960, 1560],
  /** Confetti in the suit colours: emitted over this window, falling for `CONFETTI_LIFE`. */
  confetti: [1000, 1500],
  /** Blackjack only: the pair (A and J) splits apart. */
  pair: [1120, 1760],
  /** The spin has landed on the resting pose (then a small settle). */
  land: 1980,
  /**
   * "RANK UP" slams in as a word, then the rank's name letter by letter: once the new card is nearly square
   * to us (it lands at `land`), not under it while it is still in flight, where the first letters read as ghosts.
   */
  kicker: 1560,
  name: 1740,
  /** The tier's badge pops beside the name. */
  tier: 2000,
  /** The five pips pop in, then the lit ones fill. */
  pips: 1900,
  pipLit: 2060,
  /** The bar returns in the new colour and fills the new span. */
  barIn: [2120, 2260],
  barFill: [2180, 2780],
  /** CONTINUE. */
  cont: [3000, 3200],
  end: 3240,
} as const;

/** Letter slam (the banner language): each letter 150 ms, 18 ms apart. */
export const LETTER_MS = 150;
export const LETTER_GAP_MS = 18;
export const PIP_POP_MS = 220;
export const PIP_GAP_MS = 55;
export const PIP_LIT_GAP_MS = 110;
/** How long one confetti flake falls. */
export const CONFETTI_LIFE = 2200;

/**
 * The absolute yaw at the swap and at the landing.
 *
 * 810 ≡ 90 (mod 180): edge-on, so the face changes behind the card's own
 * thickness. 1080 ≡ 0 (mod 360): square to the camera, so the new face lands
 * flat. Between them the card passes 900 — its back — and 990, edge-on again:
 * the flip reads as one continuous turn that hides the change and then shows
 * the result.
 */
export const SWAP_YAW = 810;
export const LAND_YAW = 1080;

export interface CardPose {
  /** The rank the card IS — the only rank on screen. */
  readonly rank: Rank;
  /** The finish it is struck in (follows `rank`, and so changes at the swap). */
  readonly finish: Finish;
  /** Absolute degrees about the card's vertical axis; 0 is square to the camera. */
  readonly yaw: number;
  readonly pitch: number;
  readonly scale: number;
  /** How far the card has risen off its resting place, in card heights. */
  readonly lift: number;
  /** White-hot (0..1): the swap happens at 1. */
  readonly heat: number;
  /** The light sweep's position across the face (−1.5 → 1.5; outside it is off). */
  readonly sweep: number;
}

export interface RankFrame {
  readonly t: number;
  /** Ms since the swap (negative before it). */
  readonly sinceSwap: number;
  readonly swapped: boolean;
  readonly veil: number;
  /** The recolour: the shockwave's radius as a fraction of the farthest corner (new colour inside). −1 before the swap. */
  readonly wipe: number;
  /** The shockwave's ring alpha. */
  readonly ring: number;
  /** The screen flash at the swap. */
  readonly flash: number;
  readonly rays: number;
  /** Energy gathering into the card before the swap (0..1). */
  readonly gather: number;
  readonly card: CardPose;
  /** How high the rank sits on the ladder (0 Deuce .. 1 Wild): the burst, the push-in and the confetti scale with it. */
  readonly esc: number;
  /** 1 for a Wild past the first ("Wild II" and up): a second medallion, a bigger burst. 0 otherwise. */
  readonly ascend: number;
  /** The emblem medallion behind the card: the old rank's until the swap (dim), the new one's after it. */
  readonly emblem: { readonly oldAlpha: number; readonly newAlpha: number; readonly scale: number; readonly rot: number };
  /** The contact shadow under the card: it thins and shrinks as the card rises. */
  readonly shadow: { readonly alpha: number; readonly scale: number };
  /** The fan of the rank's four suits behind the card (0 folded into the card, 1 spread). */
  readonly fan: number;
  /** The riffle's progress across the camera (0..1; outside it is not drawn). */
  readonly riffle: number;
  /** How much confetti has been emitted (0..1), and how far the oldest flake has fallen. */
  readonly confetti: number;
  /** Blackjack's pair: how far the A and the J have split (0..1). */
  readonly pair: number;
  /** The old name ("TEN V"): alpha and a small scale. */
  readonly oldName: { readonly alpha: number; readonly scale: number };
  /** Ms since the kicker's / the name's slam began (negative: not yet). */
  readonly kickerMs: number;
  readonly nameMs: number;
  /** Pips: how far each has popped in (0..1), and how many are lit (fractional: the one lighting). */
  readonly pipIn: readonly number[];
  readonly pipsLit: number;
  /** The bar: which rank it wears, its alpha, its fill (0..1) and the overflow glow. */
  readonly bar: {
    readonly rank: Rank;
    readonly alpha: number;
    readonly fill: number;
    readonly overflow: number;
  };
  readonly cont: number;
  readonly done: boolean;
}

/**
 * The yaw of the rank-up spin at `t` (degrees): a wind-up to the swap, a
 * spin-down to the landing, then one damped swing.
 */
export function rankYaw(t: number): number {
  if (t <= RANK_T.rise) return REST_YAW;
  if (t < RANK_T.swap) {
    // Quadratic, not cubic: the turn is visibly under way from its first frames.
    const u = span(t, RANK_T.rise, RANK_T.swap);
    return REST_YAW + (SWAP_YAW - REST_YAW) * u * u;
  }
  const land = SWAP_YAW + (LAND_YAW + REST_YAW - SWAP_YAW) * easeOutCubic(span(t, RANK_T.swap, RANK_T.land));
  if (t <= RANK_T.land) return land;
  // A small settle past the landing: one damped swing of 4°.
  const s = (t - RANK_T.land) / 1000;
  return land + 4 * Math.sin(s * 14) * Math.exp(-s * 9);
}

/** The idle after the landing: how long it takes to ease in (ms), the sweep's period and its length (ms). */
export const IDLE_EASE_MS = 700;
export const IDLE_SWEEP_EVERY_MS = 3200;
export const IDLE_SWEEP_MS = 1000;
/** The first idle sweep: after the landing's own sweep has passed. */
export const IDLE_SWEEP_FROM_MS = 1900;

/** The rank's place on the ladder, 0..1 (Wild II and beyond are 1). */
export const escalation = (rank: Rank): number => Math.min(1, Math.max(0, rank.index / 8));

export function rankFrameAt(plan: CeremonyPlan, t: number): RankFrame {
  const T = RANK_T;
  const esc = escalation(plan.in);
  const swapped = t >= T.swap;
  const since = t - T.swap;
  const veil = easeOutCubic(span(t, 0, T.veilIn));
  const wipeU = span(t, T.swap, T.swap + T.wipe);
  const heatUp = span(t, T.heat, T.swap);
  const heat = swapped ? 1 - easeOutCubic(span(t, T.swap, T.cool)) : heatUp * heatUp;
  const flash = swapped
    ? Math.exp(-Math.max(0, since) / 140)
    : span(t, T.swap - 70, T.swap) ** 2 * 0.9;
  const enter = easeOutBack(span(t, 0, 420), 1.4);
  // The landing's punch grows with the rank: a Wild lands harder than a Seven.
  const punch = swapped
    ? (0.16 + 0.16 * esc) * Math.exp(-since / 170) * Math.sin(Math.min(Math.PI, since / 90))
    : 0;
  const liftBase = 1.0 * easeOutCubic(span(t, T.rise, T.swap)) - 0.34 * easeOutCubic(span(t, T.swap, T.land));
  // Idle once it has landed: a slow float, a small tilt, and a sweep that comes round again.
  const idleMs = Math.max(0, t - T.land);
  const idleK = easeOutCubic(span(idleMs, 0, IDLE_EASE_MS));
  const idleS = idleMs / 1000;
  const floatY = idleK * 0.035 * Math.sin(idleS * 2.1);
  const idleYaw = idleK * 3.2 * Math.sin(idleS * 1.3);
  const idlePitch = idleK * 1.8 * Math.cos(idleS * 1.05);
  const sweepIdle = idleMs - IDLE_SWEEP_FROM_MS;
  const sweepPhase = sweepIdle < 0 ? -1 : (sweepIdle % IDLE_SWEEP_EVERY_MS) / IDLE_SWEEP_MS;
  const rank = swapped ? plan.in : plan.out;
  const pitch =
    REST_PITCH +
    (t < T.swap
      ? 8 * Math.sin(Math.PI * span(t, T.rise, T.swap))
      : -5 * Math.sin(Math.PI * span(t, T.swap, T.land))) +
    idlePitch;
  const oldIn = easeOutCubic(span(t, T.oldIn[0], T.oldIn[1]));
  const oldOut = span(t, T.oldOut[0], T.oldOut[1]);
  const pipIn = Array.from({ length: 5 }, (_, i) =>
    span(t, T.pips + i * PIP_GAP_MS, T.pips + i * PIP_GAP_MS + PIP_POP_MS),
  );
  const litU = t < T.pipLit ? 0 : (t - T.pipLit) / PIP_LIT_GAP_MS;
  const pipsLit = Math.min(plan.tierAfter, litU);
  const barOld = t < T.barOut[1];
  const barAlpha = barOld
    ? easeOutCubic(span(t, T.barOverflow[0], T.barOverflow[0] + 220)) *
      (1 - span(t, T.barOut[0], T.barOut[1]))
    : span(t, T.barIn[0], T.barIn[1]);
  const overflowU = span(t, T.barOverflow[0], T.barOverflow[1]);
  return {
    t,
    sinceSwap: since,
    swapped,
    veil,
    wipe: swapped ? easeOutCubic(wipeU) * 1.08 : -1,
    ring: swapped ? (1 - wipeU) ** 1.5 : 0,
    flash: Math.min(1, flash * (0.6 + 0.25 * esc)),
    rays: veil * (0.3 + 0.5 * heatUp + (swapped ? (0.35 + 0.5 * esc) * Math.exp(-since / 500) : 0)),
    esc,
    ascend: plan.in.name.includes(' ') ? 1 : 0,
    emblem: {
      // The old rank's medallion is a dim ghost while the light builds; the new one is struck at the swap.
      oldAlpha: swapped ? 0 : 0.34 * veil * span(t, T.veilIn, T.veilIn + 300) * (1 - heatUp),
      newAlpha: swapped ? easeOutCubic(span(t, T.swap, T.swap + 420)) : 0,
      scale: swapped ? 0.55 + 0.45 * easeOutBack(span(t, T.swap, T.swap + 620), 1.5 + esc) : 0.8 + 0.1 * span(t, 0, T.swap),
      rot: swapped ? -14 * (1 - easeOutCubic(span(t, T.swap, T.land))) + 2.2 * Math.sin(idleS * 0.6) * idleK : 0,
    },
    shadow: {
      alpha: 0.55 * easeOutCubic(span(t, 120, 520)) * (1 - 0.65 * liftBase),
      scale: 1 - 0.3 * liftBase,
    },
    gather: swapped ? 0 : span(t, T.heat - 200, T.swap),
    card: {
      rank,
      finish: finishFor(rank),
      yaw: rankYaw(t) + idleYaw,
      pitch,
      // Pushes in a touch more on the high ranks (the camera leans toward the card as it lands).
      scale: (0.72 + 0.28 * enter) * (1 + punch) * (1 + 0.05 * esc * span(t, T.swap, T.land)),
      // Rises to a card height above the tray by the swap, settling back a
      // third of the way on the landing.
      lift: liftBase + floatY,
      heat,
      sweep: !swapped ? -9 : sweepPhase >= 0 && sweepPhase <= 1 ? -1.5 + 3 * sweepPhase : t <= T.land + 500 ? -1.5 + 3 * span(t, T.land - 300, T.land + 500) : -9,
    },
    fan: swapped ? easeOutBack(span(t, T.fan[0], T.fan[1]), 1.1) : 0,
    riffle: span(t, T.riffle[0], T.riffle[1]),
    confetti: span(t, T.confetti[0], T.confetti[1]),
    pair: plan.in.index === 7 ? easeOutBack(span(t, T.pair[0], T.pair[1]), 1.2) : 0,
    oldName: { alpha: oldIn * (1 - oldOut), scale: 1 + 0.12 * oldOut },
    kickerMs: t - T.kicker,
    nameMs: t - T.name,
    pipIn,
    pipsLit,
    bar: {
      rank: barOld ? plan.out : plan.in,
      alpha: barAlpha,
      fill: barOld ? 1 : easeOutCubic(span(t, T.barFill[0], T.barFill[1])),
      overflow: barOld ? Math.sin(Math.PI * overflowU) : 0,
    },
    cont: easeOutCubic(span(t, T.cont[0], T.cont[1])),
    done: t >= T.end,
  };
}

/**
 * Every rank a frame shows, by name: the card, the old name (while it has any
 * alpha), the new name (once its slam has begun) and the bar (while it has any
 * alpha). The ceremony's contract is that this is never more than one — the
 * real Blockari bug this shape exists to make unrepresentable (tests).
 */
export function ranksShown(plan: CeremonyPlan, f: RankFrame): Set<string> {
  const s = new Set<string>([f.card.rank.name]);
  if (f.oldName.alpha > 0) s.add(plan.out.name);
  if (f.nameMs >= 0) s.add(plan.in.name);
  if (f.bar.alpha > 0) s.add(f.bar.rank.name);
  return s;
}

/** Is the card edge-on (within `eps` degrees of 90 mod 180)? The swap must be. */
export function edgeOn(yaw: number, eps = 0.5): boolean {
  const m = ((yaw % 180) + 180) % 180;
  return Math.abs(m - 90) <= eps;
}

// --- the level-up ----------------------------------------------------------------

export const LEVEL_T = {
  /** One full turn: face → edge → back → edge → face. */
  spin: 1000,
  /** The light sweep crosses the face. */
  sweep: [140, 860],
  /** The turn lands: the chip, the name and the new pip change here. */
  pip: 820,
  /** The card dips for this long before the turn (anticipation). */
  windup: 170,
  /** The ring and the count: from just before the pip lights. */
  ring: [760, 1320],
  count: [700, 1060],
  end: 1500,
} as const;

export interface LevelFrame {
  readonly t: number;
  readonly card: CardPose;
  /** Pips lit (fractional while the next one lights). */
  readonly pipsLit: number;
  /** The newly lit pip's pop (0..1..0). */
  readonly pipPop: number;
  /** The glow behind the card, 0..1..0 across the moment. */
  readonly glow: number;
  /** The ring's progress (0..1) once the turn lands; -1 before and after. */
  readonly ring: number;
  /** The level number's count-up, 0 (the old level) to 1 (the new). */
  readonly count: number;
  readonly done: boolean;
}

export function levelFrameAt(plan: CeremonyPlan, t: number): LevelFrame {
  const T = LEVEL_T;
  const u = span(t, 0, T.spin);
  const land = t > T.spin ? (t - T.spin) / 1000 : 0;
  // An overshoot that rings twice on the landing (it was a 5% blip), and a dip before the turn.
  const settle = t > T.spin ? 0.11 * Math.sin(land * 34) * Math.exp(-land * 11) : 0;
  const wind = t < T.windup ? -0.07 * Math.sin((Math.PI * Math.max(0, t)) / T.windup) : 0;
  const lift = 0.12 * Math.sin(Math.PI * u);
  const litU = t < T.pip ? 0 : 1 + (t - T.pip) / PIP_LIT_GAP_MS;
  const pipsLit = Math.min(plan.tierAfter, plan.tierBefore + Math.max(0, litU));
  const popU = span(t, T.pip, T.pip + 360);
  return {
    t,
    card: {
      rank: plan.in,
      finish: finishFor(plan.in),
      yaw: REST_YAW + 360 * easeInOutCubic(u),
      pitch: REST_PITCH - 5 * Math.sin(Math.PI * u),
      scale: 1 + 0.1 * Math.sin(Math.PI * u) + settle + wind,
      lift,
      heat: 0.22 * Math.sin(Math.PI * u) ** 2,
      sweep: -1.5 + 3 * span(t, T.sweep[0], T.sweep[1]),
    },
    pipsLit: t < 0 ? plan.tierBefore : pipsLit,
    pipPop: popU > 0 && popU < 1 ? Math.sin(Math.PI * popU) : 0,
    glow: Math.sin(Math.PI * span(t, 0, T.end)) ** 0.8,
    ring: t >= T.ring[0] && t <= T.ring[1] ? span(t, T.ring[0], T.ring[1]) : -1,
    count: span(t, T.count[0], T.count[1]),
    done: t >= T.end,
  };
}

/** The card at rest (no ceremony running). */
export function restPose(rank: Rank): CardPose {
  return {
    rank,
    finish: finishFor(rank),
    yaw: REST_YAW,
    pitch: REST_PITCH,
    scale: 1,
    lift: 0,
    heat: 0,
    sweep: -9,
  };
}

/**
 * The level-up's odometer at count `k` (0..1): the cell shows `a` rolling up out of the way and `b` rolling in, `frac`
 * of the way. It rolls through EVERY level between the old and the new (a jump of three shows three), from an empty
 * cell, eased out so it settles on the new level.
 */
export function levelRoll(before: number, after: number, k: number): { a: string; b: string; frac: number } {
  const steps = Math.max(1, after - before);
  const kk = Math.min(1, Math.max(0, k));
  const v = steps * (1 - (1 - kk) ** 2);
  const idx = Math.min(steps - 1, Math.floor(v));
  return { a: idx === 0 ? '' : String(before + idx), b: String(before + idx + 1), frac: Math.min(1, v - idx) };
}
